import React, { useMemo, useState, useEffect } from "react";
import { FileText, TrendingUp, ChevronRight } from "react-feather";
import "./GrowthIQSuggestions.css";

function GrowthIQSuggestions({
  suggestions,
  loading = false,
  onSelectItem,
  visible = true,
  selectedCompetitor,
  limitPerBucket = 10,
}) {
  const [loaderPhrase, setLoaderPhrase] = useState(0);

  const loadingPhrases = [
    "Searching knowledge store...",
    "Identifying key companies...",
  ];

  // Cycle through loading phrases
  useEffect(() => {
    if (!loading) return;

    setLoaderPhrase(0);
    const interval = setInterval(() => {
      setLoaderPhrase((prev) => (prev + 1) % loadingPhrases.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [loading]);

  // UI metadata per type
  const TYPE_META = {
    company: { title: "Company", Icon: FileText },
  };

  const toBase64 = (str = "") => {
    try {
      return window.btoa(unescape(encodeURIComponent(str)));
    } catch {
      return window.btoa(str);
    }
  };

  const handleItemClick = (item, type) => {
    if (typeof onSelectItem === "function") onSelectItem(item, type);
  };

  const getCompanyColor = (name) => {
    const colors = [
      "#E82127",
      "#76B900",
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
      "#f59e0b",
      "#10b981",
      "#06b6d4",
    ];
    const index = (name || "").length % colors.length;
    return colors[index];
  };

  // Bucket normalization
  const buckets = useMemo(() => {
    const empty = {
      companies: [],
    };
    if (!suggestions) return empty;

    if (typeof suggestions === "object" && !Array.isArray(suggestions)) {
      const companies = Array.isArray(suggestions.companies)
        ? suggestions.companies.slice(0, limitPerBucket)
        : [];

      return { companies };
    }

    if (Array.isArray(suggestions)) {
      const b = {
        companies: [],
      };
      for (const it of suggestions) {
        b.companies.push({
          name: it.companyname,
          value: it.companyid,
          ...it,
        });
      }

      b.companies = b.companies.slice(0, limitPerBucket);
      return b;
    }

    return empty;
  }, [suggestions, limitPerBucket]);

  const hasNoSuggestions = !loading && !buckets.companies?.length;

  if (!visible) return null;

  const openCompany = (c) => handleItemClick(c, "company");

  return (
    <div className="giq-suggest-panel">
      {loading && (
        <div className="giq-suggest-loading">
          <div className="giq-loader-content">
            <div className="giq-loader-spinner">
              <div className="giq-spinner-ring"></div>
              <div className="giq-spinner-ring"></div>
              <div className="giq-spinner-ring"></div>
            </div>
            <div className="giq-loader-text-wrapper">
              <span className="giq-loader-dots">
                <i></i>
                <i></i>
                <i></i>
              </span>
              <span className="giq-loader-text">
                {loadingPhrases[loaderPhrase]}
              </span>
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <>
          {/* Companies */}
          {buckets.companies.length > 0 && (
            <div className="giq-suggest-section">
              <div className="giq-suggest-section-title">COMPANIES</div>
              <ul className="giq-suggest-list">
                {buckets.companies.map((c, i) => {
                  const companyName = c.companyname;
                  const initial = companyName
                    ? companyName.charAt(0).toUpperCase()
                    : "?";
                  const color = c.color || getCompanyColor(companyName);
                  return (
                    <li
                      key={`co-${i}`}
                      className="giq-suggest-item"
                      onClick={() => openCompany(c)}
                      style={{ animationDelay: `${i * 0.05}s` }}
                    >
                      <div
                        className="giq-suggest-company-avatar"
                        style={{ backgroundColor: color }}
                      >
                        {initial}
                      </div>
                      <div className="giq-suggest-content">
                        <div className="giq-suggest-primary">{companyName}</div>
                        {/* {c.tagline && (
                          <div className="giq-suggest-description">
                            {c.tagline}
                          </div>
                        )} */}
                      </div>
                      <ChevronRight className="giq-suggest-arrow" size={18} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Empty State */}
          {!selectedCompetitor && hasNoSuggestions && (
            <div className="giq-suggest-empty">
              <div className="empty-icon-wrapper">
                <TrendingUp size={48} className="empty-icon" />
              </div>
              <h3>No suggestions found</h3>
              <p>
                No companies found in the knowledge store for this search term
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default GrowthIQSuggestions;
